import React from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {prism} from 'react-syntax-highlighter/dist/esm/styles/prism';

const RetrieveRelatedMemoryItems = ({ onSectionChange }) => {
    return (
        <div className="api-reference-layout">
            <div className="api-content">
                <div className="api-header">
                    <div className="api-method-badge post">POST</div>
                    <h1>retrieve_related_memory_items - Retrieve Related Memory Items</h1>
                    <p className="api-description">
                        Retrieve related memory items based on a query statement
                    </p>
                </div>

                <div className="api-endpoint">
                    <div className="endpoint-url">
                        <span className="method post">POST</span>
                        <span className="url">/api/v2/memory/retrieve/related-memory-items</span>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="authorization">Authorization</h2>
                    <div className="auth-item">
                        <div className="auth-header">
                            <span className="auth-name">Authorization</span>
                            <span className="auth-type">string</span>
                            <span className="auth-location">header</span>
                            <span className="required-badge">required</span>
                        </div>
                        <p>Bearer authentication header of the form Bearer &lt;token&gt;, where &lt;token&gt; is your API key.</p>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="request-body">Request Body</h2>
                    <div className="params-container">
                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">user_id</span>
                                <span className="param-type">string</span>
                                <span className="required-badge">required</span>
                            </div>
                            <p>Unique identifier for the user</p>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"user001"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">agent_id</span>
                                <span className="param-type">string</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Agent identifier, if null searches across all agents</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>null</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"gpt001"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">query</span>
                                <span className="param-type">string</span>
                                <span className="required-badge">required</span>
                            </div>
                            <p>Search query statement</p>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"hiking in mountains"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">top_k</span>
                                <span className="param-type">integer</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Maximum number of results to return</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>10</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>5</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">min_similarity</span>
                                <span className="param-type">float</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Minimum similarity threshold (0.0-1.0)</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>0.3</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>0.5</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">include_categories</span>
                                <span className="param-type">array[string]</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>List of categories to include in search</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>null</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>["personal", "hobbies"]</code>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="response">Response</h2>
                    <div className="response-status">
                        <span className="status-code success">200</span>
                        <span className="content-type">application/json</span>
                    </div>

                    <div className="params-container">
                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">related_memories</span>
                                <span className="param-type">array[RelatedMemory]</span>
                            </div>
                            <p>List of related memories</p>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">query</span>
                                <span className="param-type">string</span>
                            </div>
                            <p>Original query statement</p>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">total_found</span>
                                <span className="param-type">integer</span>
                            </div>
                            <p>Total number of memories found</p>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">search_params</span>
                                <span className="param-type">object</span>
                            </div>
                            <p>Search parameters used</p>
                        </div>
                    </div>

                    <div className="response-schema">
                        <h3>RelatedMemory Structure:</h3>
                        <div className="params-container">
                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">memory</span>
                                    <span className="param-type">MemoryItem</span>
                                </div>
                                <p>Memory item details</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">user_id</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>User ID</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">agent_id</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>Agent ID</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">similarity_score</span>
                                    <span className="param-type">float</span>
                                </div>
                                <p>Similarity score (0.0-1.0)</p>
                            </div>
                        </div>

                        <h3>MemoryItem Structure:</h3>
                        <div className="params-container">
                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">memory_id</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>Unique memory identifier</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">category</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>Memory category</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">content</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>Memory content</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">happened_at</span>
                                    <span className="param-type">datetime</span>
                                </div>
                                <p>When the memory occurred</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">created_at</span>
                                    <span className="param-type">datetime</span>
                                </div>
                                <p>Memory creation time</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">updated_at</span>
                                    <span className="param-type">datetime</span>
                                </div>
                                <p>Memory last update time</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="api-demo-sidebar">
                <div className="demo-sticky">
                    <div className="demo-header">
                        <div className="demo-tabs">
                            <button className="demo-tab active">Python</button>
                            <button className="demo-tab">JavaScript</button>
                            <button className="demo-tab">cURL</button>
                        </div>
                    </div>

                    <div className="demo-content">
                        <h3 id="examples">Request Example</h3>
                        <SyntaxHighlighter
                            language="python"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`import os
from memu import MemuClient

# Initialize MemU client
memu_client = MemuClient(
    base_url="https://api.memu.so",
    api_key=os.getenv("MEMU_API_KEY")
)

# Basic search for related memories
response = memu_client.retrieve_related_memory_items(
    user_id="user001",
    agent_id="gpt001",
    query="hiking in mountains",
    top_k=5,
    min_similarity=0.5
)

print(f"Found {response.total_found} related memories")
for related_memory in response.related_memories:
    memory = related_memory.memory
    print(f"Similarity: {related_memory.similarity_score:.2f}")
    print(f"Content: {memory.content}")
    print(f"Category: {memory.category}")
    print("---")

# Search with category filtering
filtered_response = memu_client.retrieve_related_memory_items(
    user_id="user001",
    query="favorite movies",
    include_categories=["entertainment", "personal"],
    top_k=3
)

for related_memory in filtered_response.related_memories:
    print(f"Memory: {related_memory.memory.content}")
    print(f"Score: {related_memory.similarity_score}")`}
                        </SyntaxHighlighter>
                    </div>

                    <div className="response-section">
                        <div className="response-header">
                            <span className="response-status-code">200</span>
                            <span>Response Example</span>
                        </div>
                        <SyntaxHighlighter
                            language="json"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`{
  "related_memories": [
    {
      "memory": {
        "memory_id": "mem_abc123",
        "category": "hobbies",
        "content": "User loves hiking in the Rocky Mountains every summer",
        "happened_at": "2024-01-15T10:30:00Z",
        "created_at": "2024-01-15T10:35:00Z",
        "updated_at": "2024-01-15T10:35:00Z"
      },
      "user_id": "user001",
      "agent_id": "gpt001",
      "similarity_score": 0.92
    },
    {
      "memory": {
        "memory_id": "mem_def456",
        "category": "personal",
        "content": "Prefers mountain trails over beach walks",
        "happened_at": "2024-01-10T14:20:00Z",
        "created_at": "2024-01-10T14:25:00Z",
        "updated_at": "2024-01-10T14:25:00Z"
      },
      "user_id": "user001",
      "agent_id": "gpt001",
      "similarity_score": 0.78
    }
  ],
  "query": "hiking in mountains",
  "total_found": 2,
  "search_params": {
    "top_k": 5,
    "min_similarity": 0.5,
    "include_categories": null
  }
}`}
                        </SyntaxHighlighter>
                    </div>

                    <div className="response-section">
                        <div className="response-header">
                            <span className="response-status-code">200</span>
                            <span>Filtered Search Response</span>
                        </div>
                        <SyntaxHighlighter
                            language="json"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`{
  "related_memories": [
    {
      "memory": {
        "memory_id": "mem_ghi789",
        "category": "entertainment",
        "content": "Favorite movie genre is sci-fi, especially space exploration films",
        "happened_at": "2024-01-12T16:45:00Z",
        "created_at": "2024-01-12T16:50:00Z",
        "updated_at": "2024-01-12T16:50:00Z"
      },
      "user_id": "user001",
      "agent_id": "gpt001",
      "similarity_score": 0.85
    }
  ],
  "query": "favorite movies",
  "total_found": 1,
  "search_params": {
    "top_k": 3,
    "min_similarity": 0.3,
    "include_categories": ["entertainment", "personal"]
  }
}`}
                        </SyntaxHighlighter>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RetrieveRelatedMemoryItems;

